import React from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Textarea from '../../../components/ui/Textarea';
import FormSection from '../../../components/ui/FormSection';
import FileField from '../../../components/ui/FileField';

/**
 * Step one of the setup wizard.
 *
 * This was thirteen fields in one flat column — name, type, subject, website,
 * location, venue, dates, description, banner, call for papers — every one
 * weighted the same, with two ~80-line copy-pasted upload blocks at the bottom.
 * Nothing said where one concern ended and the next began.
 *
 * Now it's four groups that match how an organiser actually thinks about a
 * conference: what it is, where and when, what it's about, and the optional
 * media. The last group is collapsed by default, so the required path to a
 * publishable conference is visibly short.
 */
const BasicInformationForm = ({
  formData,
  onChange,
  errors,
  cfpFile,
  onCfpFileChange,
  bannerFile,
  onBannerFileChange,
}) => {
  const { t } = useTranslation('conferences');
  const b = (key, opts) => t(`setupWizard.basicInfo.${key}`, opts);

  const conferenceTypeOptions = ['academic', 'industry', 'workshop', 'symposium', 'seminar']?.map((value) => ({
    value,
    label: b(`conferenceType.options.${value}`),
  }));

  const subjectAreaOptions = ['medicine', 'ai', 'business', 'engineering', 'science', 'social', 'humanities', 'technology']?.map((value) => ({
    value,
    label: b(`subjectArea.options.${value}`),
  }));

  const locationTypeOptions = [
    { value: 'virtual', label: b('locationType.options.virtual') },
    { value: 'hybrid', label: b('locationType.options.hybrid') },
    { value: 'in-person', label: b('locationType.options.inPerson') },
  ];

  const today = new Date()?.toISOString()?.split('T')?.[0];
  const pair = 'grid grid-cols-1 lg:grid-cols-2 gap-4';

  return (
    <div className="space-y-6">
      <FormSection
        title={b('groups.identity.title', 'What is it called?')}
        description={b('groups.identity.description', 'The full name appears on the public page; the short name is used in tables, emails and file names.')}
        hasError={Boolean(errors?.conferenceName || errors?.shortName || errors?.conferenceType || errors?.subjectArea)}
      >
        <div className={pair}>
          <Input
            label={b('conferenceName.label')}
            type="text"
            name="conferenceName"
            placeholder={b('conferenceName.placeholder')}
            value={formData?.conferenceName}
            onChange={onChange}
            error={errors?.conferenceName}
            required
            description={b('conferenceName.description')}
          />
          <Input
            label={b('shortName.label')}
            type="text"
            name="shortName"
            placeholder={b('shortName.placeholder')}
            value={formData?.shortName}
            onChange={onChange}
            error={errors?.shortName}
            required
            description={b('shortName.description')}
          />
        </div>
        <div className={pair}>
          <Select
            label={b('conferenceType.label')}
            options={conferenceTypeOptions}
            value={formData?.conferenceType}
            onChange={(value) => onChange({ target: { name: 'conferenceType', value } })}
            error={errors?.conferenceType}
            required
            placeholder={b('conferenceType.placeholder')}
          />
          <Select
            label={b('subjectArea.label')}
            options={subjectAreaOptions}
            value={formData?.subjectArea}
            onChange={(value) => onChange({ target: { name: 'subjectArea', value } })}
            error={errors?.subjectArea}
            required
            searchable
            placeholder={b('subjectArea.placeholder')}
          />
        </div>
      </FormSection>

      <FormSection
        title={b('groups.whereWhen.title', 'Where and when?')}
        description={b('groups.whereWhen.description', 'Venue fields appear only if people are attending in person.')}
        hasError={Boolean(errors?.locationType || errors?.venueName || errors?.location || errors?.startDate || errors?.endDate)}
      >
        <div className={pair}>
          <Select
            label={b('locationType.label')}
            options={locationTypeOptions}
            value={formData?.locationType}
            onChange={(value) => onChange({ target: { name: 'locationType', value } })}
            error={errors?.locationType}
            required
            placeholder={b('locationType.placeholder')}
          />
          <Input
            label={b('website.label')}
            type="url"
            name="website"
            placeholder={b('website.placeholder')}
            value={formData?.website}
            onChange={onChange}
            error={errors?.website}
            description={b('website.description')}
          />
        </div>

        {/* Progressive disclosure driven by the answer above: a virtual
            conference has no venue, so asking for one is noise. */}
        {formData?.locationType !== 'virtual' && (
          <div className={pair}>
            <Input
              label={b('venueName.label')}
              type="text"
              name="venueName"
              placeholder={b('venueName.placeholder')}
              value={formData?.venueName}
              onChange={onChange}
              error={errors?.venueName}
              description={b('venueName.description')}
            />
            <Input
              label={b('location.label')}
              type="text"
              name="location"
              placeholder={b('location.placeholder')}
              value={formData?.location}
              onChange={onChange}
              error={errors?.location}
              description={b('location.description')}
            />
          </div>
        )}

        <div className={pair}>
          <Input
            label={b('startDate.label')}
            type="date"
            name="startDate"
            value={formData?.startDate}
            onChange={onChange}
            error={errors?.startDate}
            required
            min={today}
          />
          <Input
            label={b('endDate.label')}
            type="date"
            name="endDate"
            value={formData?.endDate}
            onChange={onChange}
            // The end date can't precede the start, and saying so at the field
            // beats letting the wizard reject it a step later.
            error={
              errors?.endDate ||
              (formData?.startDate && formData?.endDate && formData.endDate < formData.startDate
                ? b('endDate.beforeStart', 'The end date is before the start date.')
                : undefined)
            }
            required
            min={formData?.startDate || today}
          />
        </div>
      </FormSection>

      <FormSection
        title={b('groups.about.title', 'What is it about?')}
        description={b('groups.about.description', 'This is what authors read before deciding whether to submit.')}
        hasError={Boolean(errors?.description)}
      >
        <Textarea
          label={b('description.label')}
          name="description"
          rows={6}
          maxLength={2000}
          placeholder={b('description.placeholder')}
          value={formData?.description || ''}
          onChange={onChange}
          error={errors?.description}
          description={b('description.hint')}
          required
        />
        <Textarea
          label={b('callForPapers.label')}
          name="callForPapers"
          rows={8}
          maxLength={5000}
          placeholder={b('callForPapers.placeholder')}
          value={formData?.callForPapers || ''}
          onChange={onChange}
          description={b('callForPapers.hint')}
        />
      </FormSection>

      <FormSection
        title={b('groups.media.title', 'Banner and documents')}
        description={b('groups.media.description', 'Both are optional. You can add them any time before you publish.')}
        optional
        collapsible
        defaultOpen={Boolean(bannerFile || cfpFile)}
      >
        <FileField
          label={b('banner.label')}
          hint={b('banner.hint')}
          file={bannerFile}
          onChange={onBannerFileChange}
          accept="image/jpeg,image/png,image/webp"
          maxSizeMb={5}
          preview
          icon="Image"
        />
        <FileField
          label={b('cfpDoc.label')}
          hint={b('cfpDoc.hint')}
          file={cfpFile}
          onChange={onCfpFileChange}
          accept=".pdf"
          maxSizeMb={10}
          icon="FileText"
        />
      </FormSection>
    </div>
  );
};

export default BasicInformationForm;
